import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ViewTransitionInfo } from '@angular/router';
import { prefersReducedMotion } from '@core/dom/reduced-motion.utils';
import { NavigationDirectionService } from './services/navigation-direction.service';
import { ViewTransitionRegistryService } from './services/view-transition-registry.service';

export function handleViewTransition({ transition, from, to }: ViewTransitionInfo): void {
    const navDirection = inject(NavigationDirectionService);
    const doc = inject(DOCUMENT);
    const registry = inject(ViewTransitionRegistryService);

    // Respect OS-level reduced-motion preference — skip the whole view transition animation.
    // Registered handlers still run: they only add/remove classes on documentElement (verified —
    // see ViewTransitionRegistryService.applyAll consumers). Those classes are referenced by
    // `::view-transition-*` pseudo-elements which never paint when skipTransition is called,
    // so applyAll + cleanupAll are no-ops visually under reduced-motion. No side effects leak.
    if (prefersReducedMotion()) {
        transition.skipTransition();
    }

    const fromUrl = routeSnapshotToUrl(from);
    const toUrl = routeSnapshotToUrl(to);

    registry.applyAll(fromUrl, toUrl, doc.documentElement, transition);

    transition.finished.finally(() => {
        navDirection.resetDirection();
        registry.cleanupAll(doc.documentElement);
    });
}

function routeSnapshotToUrl(root: ActivatedRouteSnapshot): string {
    let snapshot = root;

    while (snapshot.firstChild) {
        snapshot = snapshot.firstChild;
    }

    return '/' + snapshot.pathFromRoot
        .map(segment => segment.url.map(urlSegment => urlSegment.path).join('/'))
        .filter(Boolean)
        .join('/');
}
